import { useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useImageChange } from '@/hooks'
import { readExcelFile, validateExcelFile } from '@/lib/xlsx'
import {
  validateTrolleyDataIntegrity,
  validateTrolleyDrawerLimit,
  type TrolleySheetData,
} from '@/utils/tool-management/tool-management.util'

import {
  getExcelReadErrorKey,
  getExcelValidationErrorKey,
  isExcelFile,
} from '@/utils/tool-management/excel-file.util'

export function useToolImportModal() {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const {
    uploadedImages,
    error: imageError,
    handleImageChange,
    removeImage,
    clearImages,
    setError: setImageError,
  } = useImageChange()

  const [uploadedFile, setUploadedFile] = useState<Nullable<File>>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState('')
  const [toolManagementData, setToolManagementData] =
    useState<Nullable<TrolleySheetData>>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isDraggingImage, setIsDraggingImage] = useState(false)

  const handleFileProcessing = useCallback(
    async (file: File) => {
      setError('')
      setIsUploading(true)
      setUploadedFile(file)
      setUploadProgress(0)
      setToolManagementData(null)

      const validation = validateExcelFile(file)
      if (!validation.valid) {
        if (validation.error) {
          const errorKey = getExcelValidationErrorKey(validation.error)
          setError(t(errorKey))
        } else {
          setError(t('toolManagement.invalidFile'))
        }
        setIsUploading(false)
        setUploadedFile(null)
        return
      }

      try {
        const result = await readExcelFile(file, {
          onProgress: setUploadProgress,
        })

        if (!validateTrolleyDataIntegrity(result.data)) {
          console.warn(
            'Import failed: missing required fields (Pn, ChineseName, Description, or Quantity)'
          )
          setError(t('toolManagement.invalidFile'))
          setToolManagementData(null)

          setIsUploading(false)
          return
        }

        if (!validateTrolleyDrawerLimit(result.data)) {
          setError(t('toolManagement.drawersMustNotExceed', { count: 100 }))
          setToolManagementData(null)
          setIsUploading(false)
          return
        }

        setToolManagementData(result.data)
        setIsUploading(false)
      } catch (error) {
        console.error('Upload failed:', error)
        const errorKey = getExcelReadErrorKey(error)
        setError(t(errorKey))
        setIsUploading(false)
        setUploadedFile(null)
      }
    },
    [t]
  )

  const handleFileChange = (e: React.FormEvent<EventTarget>) => {
    const files = (e.target as HTMLInputElement).files
    if (files && files.length > 0) {
      handleFileProcessing(files[0])
    }
    ;(e.target as HTMLInputElement).value = ''
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isUploading && !uploadedFile) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (isUploading || uploadedFile) {
      return
    }

    const files = e.dataTransfer.files
    if (files.length > 0) {
      const file = files[0]

      if (isExcelFile(file)) {
        handleFileProcessing(file)
      } else {
        setError(t('toolManagement.invalidFileType'))
      }
    }
  }

  const triggerFileInput = () => {
    fileInputRef.current?.click()
  }

  const triggerImageInput = () => {
    imageInputRef.current?.click()
  }

  const handleImageChangeWithValidation = (e: React.FormEvent<EventTarget>) => {
    const files = (e.target as HTMLInputElement).files
    if (!files) return

    const imageFiles: File[] = []
    let hasInvalidFormat = false
    let hasInvalidSize = false

    Array.from(files).forEach(file => {
      const isValidFormat =
        file.name.match(/\.(png|jpg|jpeg)$/i) ||
        file.type === 'image/png' ||
        file.type === 'image/jpeg' ||
        file.type === 'image/jpg'

      if (!isValidFormat) {
        hasInvalidFormat = true
        return
      }

      const MAX_SIZE = 5 * 1024 * 1024
      if (file.size > MAX_SIZE) {
        hasInvalidSize = true
        return
      }

      imageFiles.push(file)
    })

    if (hasInvalidFormat) {
      setImageError(t('toolManagement.invalidImageFileType'))
      ;(e.target as HTMLInputElement).value = ''
      return
    }

    if (hasInvalidSize) {
      setImageError(t('toolManagement.imageSizeTooLarge'))
      ;(e.target as HTMLInputElement).value = ''
      return
    }

    if (imageFiles.length > 0) {
      const dataTransfer = new DataTransfer()
      imageFiles.forEach(file => dataTransfer.items.add(file))

      const syntheticInput = document.createElement('input')
      syntheticInput.type = 'file'
      syntheticInput.files = dataTransfer.files

      const syntheticEvent = {
        target: syntheticInput,
      } as unknown as React.FormEvent<EventTarget>

      handleImageChange(syntheticEvent)
    }
  }

  const handleImageDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(true)
  }

  const handleImageDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(false)
  }

  const handleImageDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(false)

    const files = e.dataTransfer.files
    if (files.length > 0) {
      const imageFiles: File[] = []
      let hasInvalidFormat = false
      let hasInvalidSize = false

      Array.from(files).forEach(file => {
        const isValidFormat =
          file.name.match(/\.(png|jpg|jpeg)$/i) ||
          file.type === 'image/png' ||
          file.type === 'image/jpeg' ||
          file.type === 'image/jpg'

        if (!isValidFormat) {
          hasInvalidFormat = true
          return
        }

        const MAX_SIZE = 5 * 1024 * 1024
        if (file.size > MAX_SIZE) {
          hasInvalidSize = true
          return
        }

        imageFiles.push(file)
      })

      if (hasInvalidFormat) {
        setImageError(t('toolManagement.invalidImageFileType'))
        return
      }

      if (hasInvalidSize) {
        setImageError(t('toolManagement.imageSizeTooLarge'))
        return
      }

      if (imageFiles.length > 0) {
        const dataTransfer = new DataTransfer()
        imageFiles.forEach(file => dataTransfer.items.add(file))

        const syntheticInput = document.createElement('input')
        syntheticInput.type = 'file'
        syntheticInput.files = dataTransfer.files

        const syntheticEvent = {
          target: syntheticInput,
        } as unknown as React.FormEvent<EventTarget>

        handleImageChange(syntheticEvent)
      }
    }
  }

  const handleRemoveFile = () => {
    setUploadedFile(null)
    setError('')
    setToolManagementData(null)
  }

  const handleCloseModal = () => {
    setUploadedFile(null)
    clearImages()
    setError('')
    setUploadProgress(0)
  }

  const handleRetryProcessing = () => {
    triggerFileInput()
  }

  return {
    fileInputRef,
    imageInputRef,
    uploadedImages,
    imageError,
    uploadedFile,
    uploadProgress,
    isUploading,
    error,
    toolManagementData,
    isDragging,
    isDraggingImage,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    triggerFileInput,
    triggerImageInput,
    handleImageChange: handleImageChangeWithValidation,
    handleImageDragOver,
    handleImageDragLeave,
    handleImageDrop,
    removeImage,
    handleRemoveFile,
    handleRetryProcessing,
    handleCloseModal,
    setUploadedFile,
  }
}
